import { ErrorResponse } from '@opositandopn/http';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { useAxios } from '../../../context/AxiosContext';
import { FALLBACK_ERROR_CODE } from '../../../utils/api';
import { getIanaTimeZoneId } from '../../../utils/date';

export interface ConsentViewModel {
  ad: boolean;
  analytics: boolean;
  functionality: boolean;
  personalization: boolean;
  security: boolean;
}

export interface NotificationsViewModel {
  email: boolean;
  push: boolean;
}

export interface UserAgreementViewModel {
  accepted: boolean;
  version: number;
}

export interface BillingAddressViewModel {
  name: string;
  street: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
  companyName: string;
  vatIdentificationNumber: string;
}

export interface SchoolViewModel {
  id: string;
  name: string;
}

export interface UserPreviewModel {
  id: string;
  identityProviderId: string;
  email: string;
  active: boolean;
  banned: boolean;
  username?: string;
  name?: string;
  avatarUrl?: string;
  credits?: number;
  school?: SchoolViewModel;
}

export interface UserViewModel extends UserPreviewModel {
  role: string;
  gender?: 'male' | 'female';
  bio?: string;
  notifications?: NotificationsViewModel;
  consent?: ConsentViewModel;
  userAgreement?: UserAgreementViewModel;
  billingAddress?: BillingAddressViewModel;
  timeZone?: string;
}

export const useGetSelf = (enabled?: boolean) => {
  const { instance } = useAxios();

  async function queryFn(): Promise<UserViewModel> {
    const response = await instance.get<UserViewModel>(`v2/users/me`);

    return response.data;
  }

  return useQuery({
    queryKey: ['getSelf'],
    queryFn,
    enabled,
  });
};

export interface UpdateUserRequest {
  id: string;
  gender?: 'male' | 'female';
}

export const useUpdateUser = () => {
  const { instance } = useAxios();
  const queryClient = useQueryClient();

  async function mutationFn(request: UpdateUserRequest) {
    const response = await instance.patch(`/v2/users/${request.id}`, request);

    return response.data;
  }

  return useMutation<void, AxiosError<ErrorResponse>, UpdateUserRequest>({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['getSelf'],
      });
    },
  });
};

export interface AssessmentAttemptMetricsViewModel {
  grade: number;
  elapsedTime: number;
  submittedAt: Date;
  totalQuestions: number;
  correctAnswers: number;
  incorrectAnswers: number;
}

export interface UserMetricsViewModel {
  questionAverageTime: number;
  averageScore: number;
  total: number;
  trend: number;
  metrics: AssessmentAttemptMetricsViewModel[];
}

export type GetUserMetricsRequest = {
  userId: string;
  subjectId: string;
  perspectives?: string[];
};

export const useGetUserMetrics = (
  request: GetUserMetricsRequest,
  enabled?: boolean,
) => {
  const { instance } = useAxios();

  async function queryFn(): Promise<UserMetricsViewModel> {
    const params = new URLSearchParams({
      subjectId: request.subjectId,
    });

    if (request.perspectives && request.perspectives.length > 0) {
      request.perspectives.forEach((perspective) => {
        params.append('perspective', perspective);
      });
    }

    const response = await instance.get<UserMetricsViewModel>(
      `/v2/users/${request.userId}/metrics/mc?${params.toString()}`,
    );

    return response.data;
  }

  return useQuery({
    queryKey: [
      'getUserMetrics',
      request.userId,
      request.subjectId,
      request.perspectives,
    ],
    queryFn,
    enabled,
  });
};

export interface StreakDayViewModel {
  date: Date;
  status: string;
}

export interface StreakViewModel {
  days: StreakDayViewModel[];
  protectors: number;
  currentDays: number;
  bestDays: number;
  totalActiveDays: number;
  isActiveToday: boolean;
  isDisabledToday: boolean;
}

export type GetStreakRequest = {
  userId: string;
};

export const useGetStreak = (request: GetStreakRequest) => {
  const { instance } = useAxios();

  async function queryFn(): Promise<StreakViewModel> {
    const timeZoneId = getIanaTimeZoneId();
    const url = `/v2/users/${request.userId}/metrics/streak?timeZoneId=${encodeURIComponent(timeZoneId)}`;
    const response = await instance.get<StreakViewModel>(url);

    return response.data;
  }

  const timeZoneId = getIanaTimeZoneId();

  return useQuery({
    queryKey: ['getStreak', request.userId, timeZoneId],
    queryFn,
  });
};

export type ResetUserMetricsRequest = {
  userId: string;
  subjectId: string;
};

export const useResetUserMetrics = () => {
  const { instance } = useAxios();
  const queryClient = useQueryClient();

  async function mutationFn(request: ResetUserMetricsRequest) {
    const response = await instance.post(
      `/v2/users/${request.userId}/metrics/mc/reset`,
      {
        subjectId: request.subjectId,
      },
    );

    return response.data;
  }

  return useMutation<void, AxiosError<ErrorResponse>, ResetUserMetricsRequest>({
    mutationFn,
    onSuccess: (_, request) => {
      queryClient.invalidateQueries({
        queryKey: ['getUserMetrics', request.userId, request.subjectId],
      });
    },
  });
};

export type RedeemProtectorsRequest = {
  userId: string;
  amount: number;
};

export const useRedeemProtectors = () => {
  const { instance } = useAxios();
  const queryClient = useQueryClient();

  async function mutationFn(request: RedeemProtectorsRequest) {
    const response = await instance.post(
      `/v2/users/${request.userId}/metrics/streak/protectors/redeem`,
      {
        amount: request.amount,
      },
    );

    return response.data;
  }

  return useMutation<void, AxiosError<ErrorResponse>, RedeemProtectorsRequest>({
    mutationFn,
    onSuccess: (_, request) => {
      // Invalidate all streak queries for this user (protectors changed)
      queryClient.invalidateQueries({
        queryKey: ['getStreak', request.userId],
      });
      // Invalidate user data (credits changed)
      queryClient.invalidateQueries({
        queryKey: ['getSelf'],
      });
    },
    meta: {
      errorMessages: {
        [FALLBACK_ERROR_CODE]: {
          title: '¡Ups!',
          message: 'No se han podido canjear los puntos',
        },
      },
    },
  });
};
